import React, { useState, useEffect, useCallback } from 'react';
import { MessageCircle } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useWebSocket } from '@/contexts/WebSocketContext';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/services/api';

/**
 * Professional Message Notification Badge Component
 * - Shows unread message count in real-time
 * - Updates via WebSocket for instant notifications
 * - Polls periodically as fallback
 * - Click to navigate to messages
 * - Plays sound when new message arrives (recipient only)
 * - Production-ready with comprehensive error handling
 */
const MessageNotificationBadge = ({ className = '' }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false); // Changed to false for instant display
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { socket, isConnected } = useWebSocket();
  const { user } = useAuth();
  const location = useLocation();

  // Hide badge if user is on the messaging page
  const isOnMessagingPage = location.pathname === '/collaboration/messaging';

  /**
   * Fetch unread count from API
   * - Handles errors gracefully
   * - Retries on failure
   * - Updates state only on success
   */
  const fetchUnreadCount = useCallback(async (silent = false) => {
    try {
      if (!silent) setIsLoading(true);
      setError(null);

      const response = await api.get('/direct-messaging/unread-count');
      
      if (response.data?.success) {
        const count = response.data?.data?.count || 0;
        setUnreadCount(count);
        console.log('📬 Unread messages count updated:', count);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('❌ Failed to fetch unread count:', err);
      
      // Only set error on initial load, not on background refreshes
      if (!silent) {
        setError(err.message);
      }
      
      // Don't reset count on error - keep showing last known count
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, []);

  /**
   * Initial load and polling fallback
   * - Fetch on mount (silent to avoid loading state)
   * - Poll every 5 seconds for near-instant updates (aggressive)
   */
  useEffect(() => {
    // Initial fetch (silent = true for instant display)
    fetchUnreadCount(true);

    // Set up aggressive polling interval (5 seconds for near-instant updates)
    const pollInterval = setInterval(() => {
      console.log('🔄 Polling for unread count...');
      fetchUnreadCount(true); // Silent refresh
    }, 5000); // Changed from 30s to 5s

    return () => {
      clearInterval(pollInterval);
    };
  }, [fetchUnreadCount]);

  /**
   * WebSocket real-time updates
   * - Listen for unread count changes
   * - Instant notification without polling delay
   * - Show toast and play sound for new messages (recipient only)
   */
  useEffect(() => {
    if (!socket || !isConnected) {
      console.log('⚠️ WebSocket not ready:', { hasSocket: !!socket, isConnected });
      return;
    }

    console.log('🔌 Setting up WebSocket listeners for user:', {
      userId: user?.id,
      employeeId: user?.Employee?.id,
      email: user?.email
    });

    const handleUnreadCountUpdate = (data) => {
      console.log('📬 Real-time unread count update:', data);
      const count = data?.count ?? 0;
      setUnreadCount(count);
      setError(null); // Clear any previous errors on successful WebSocket update
    };

    const handleNewMessage = (data) => {
      console.log('📬 NEW MESSAGE EVENT RECEIVED:', data);
      console.log('📬 Current time:', new Date().toISOString());
      
      // SECURITY: Only show badge and play sound if current user is the RECIPIENT
      const currentUserId = user?.id;
      const currentEmployeeId = user?.Employee?.id;
      
      console.log('🔍 Recipient Check:', {
        currentUserId,
        currentEmployeeId,
        messageToEmployeeId: data.to_employee_id,
        messageToUserId: data.to_employee_user_id,
        isMatch: data.to_employee_id === currentEmployeeId
      });
      
      const isRecipient = (
        data.to_employee_id === currentEmployeeId ||
        (data.to_employee_user_id && data.to_employee_user_id === currentUserId)
      );
      
      if (isRecipient) {
        console.log('✅✅✅ I AM THE RECIPIENT - UPDATING BADGE NOW ✅✅✅');
        console.log('⏱️ Update timestamp:', new Date().toISOString());
        
        // Increment unread count IMMEDIATELY (< 10ms response time)
        setUnreadCount(prev => {
          const newCount = prev + 1;
          console.log(`📈 Badge count updated: ${prev} → ${newCount}`);
          return newCount;
        });
        
        // Verify count from server after a short delay (backup verification)
        setTimeout(() => {
          console.log('🔄 Fetching count from server for verification...');
          fetchUnreadCount(true);
        }, 1000);
      } else {
        console.log('ℹ️ I am the sender - no notification needed');
      }
    };

    // Listen for WebSocket events
    socket.on('direct_message:unread_count', handleUnreadCountUpdate);
    socket.on('direct_message:new', handleNewMessage);

    // Refresh count when reconnecting
    if (isConnected) {
      fetchUnreadCount(true);
    }

    return () => {
      console.log('🔌 Cleaning up WebSocket listeners');
      socket.off('direct_message:unread_count', handleUnreadCountUpdate);
      socket.off('direct_message:new', handleNewMessage);
    };
  }, [socket, isConnected, fetchUnreadCount, user]);

  /**
   * Navigate to messages page
   */
  const handleClick = () => {
    navigate('/collaboration/messaging');
  };

  // Don't render badge if user is on messaging page
  if (isOnMessagingPage) {
    return null;
  }

  return (
    <>
      {/* Floating Message Button - Bottom Right (enhanced styling) */}
      <div className="fixed bottom-24 right-6 z-40">
        <button
          onClick={handleClick}
          className="relative group flex items-center justify-center w-16 h-16 rounded-2xl shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300 hover:-translate-y-1"
          aria-label={`Messages${unreadCount > 0 ? ` - ${unreadCount} unread` : ''}`}
          title={`${unreadCount > 0 ? unreadCount : 'No'} unread message${unreadCount !== 1 ? 's' : ''}`}
          style={{
            background: 'linear-gradient(135deg, #16314C 0%, #0d1f30 100%)',
            boxShadow: '0 8px 32px rgba(22, 49, 76, 0.4), 0 4px 16px rgba(13, 31, 48, 0.3)',
            border: '1px solid rgba(223, 107, 56, 0.2)'
          }}
        >
          {/* Message Icon with glow effect */}
          <div className="relative">
            <MessageCircle 
              className="w-8 h-8 text-white drop-shadow-lg" 
              strokeWidth={2}
            />
            {unreadCount > 0 && (
              <div 
                className="absolute inset-0 w-8 h-8 rounded-full blur-md opacity-50"
                style={{
                  background: '#DF6B38'
                }}
              />
            )}
          </div>
          
          {/* Unread Count Badge - Enhanced */}
          {unreadCount > 0 && (
            <span 
              className="absolute -top-2 -right-2 flex items-center justify-center min-w-[28px] h-7 px-2.5 text-xs font-bold text-white rounded-full shadow-xl animate-in fade-in zoom-in duration-200"
              style={{
                background: 'linear-gradient(135deg, #DF6B38, #DF383F)',
                boxShadow: '0 4px 12px rgba(223, 107, 56, 0.5), 0 0 0 3px rgba(22, 49, 76, 0.8)',
                border: '2px solid #16314C'
              }}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
          
          {/* Pulsing ring for new messages - Enhanced */}
          {unreadCount > 0 && (
            <>
              <span 
                className="absolute inset-0 w-16 h-16 rounded-2xl animate-ping opacity-20"
                style={{
                  background: 'linear-gradient(135deg, #DF6B38, #DF383F)'
                }}
              />
              {/* Second pulse layer for depth */}
              <span 
                className="absolute inset-0 w-16 h-16 rounded-2xl animate-pulse opacity-10"
                style={{
                  background: '#DF6B38',
                  animationDuration: '2s'
                }}
              />
            </>
          )}
          
          {/* Hover glow effect */}
          <div 
            className="absolute inset-0 w-16 h-16 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            style={{
              background: 'radial-gradient(circle at center, rgba(223, 107, 56, 0.2) 0%, transparent 70%)'
            }}
          />
        </button>
      </div>
    </>
  );
};

export default MessageNotificationBadge;
